/**
 * The art library, from the browser.
 *
 * Reads come straight from Supabase with the publishable key (SELECT is the
 * only thing that key can do). Writes go to the Worker, which holds the secret.
 * Same split as `careerStore.ts`.
 *
 * THE LIBRARY IS LOADED ONCE AND HELD. A race asks for a picture on almost
 * every event — measured at 130+ events per race — and a query per moment
 * would be absurd. The whole table is a few hundred rows of metadata; it is
 * fetched once, cached, and selected from in memory.
 */

import { supabase } from './supabase.ts';
import type {
  ArtImage,
  Condition,
  Intensity,
  Rarity,
  SceneTag,
  ShotType,
  TrackCategory,
} from '../domain/art/scenes.ts';

const BUCKET = 'art';

/** The row as Postgres stores it. */
interface ArtRow {
  id: string;
  storage_path: string;
  scenes: string[];
  shot: string | null;
  intensity: string | null;
  track_categories: string[];
  conditions: string[];
  rarity: string | null;
  title: string | null;
  credit: string | null;
  notes: string | null;
  original_name: string | null;
  width: number | null;
  height: number | null;
}

export interface StoredArtImage extends ArtImage {
  storagePath: string;
  originalName: string | null;
  width: number | null;
  height: number | null;
}

function publicUrl(storagePath: string): string {
  return supabase.storage.from(BUCKET).getPublicUrl(storagePath).data.publicUrl;
}

function toImage(r: ArtRow): StoredArtImage {
  return {
    id: r.id,
    url: publicUrl(r.storage_path),
    storagePath: r.storage_path,
    scenes: r.scenes as SceneTag[],
    shot: (r.shot as ShotType | null) ?? null,
    intensity: (r.intensity as Intensity | null) ?? null,
    trackCategories: (r.track_categories ?? []) as TrackCategory[],
    conditions: (r.conditions ?? []) as Condition[],
    rarity: (r.rarity as Rarity | null) ?? 'common',
    title: r.title ?? undefined,
    credit: r.credit ?? undefined,
    notes: r.notes ?? undefined,
    originalName: r.original_name,
    width: r.width,
    height: r.height,
  };
}

let cache: StoredArtImage[] | null = null;
let inFlight: Promise<StoredArtImage[]> | null = null;

/** Load the library, once. Subsequent calls return the cached array. */
export async function loadLibrary(force = false): Promise<StoredArtImage[]> {
  if (!force && cache) return cache;
  if (!force && inFlight) return inFlight;

  inFlight = (async () => {
    const { data, error } = await supabase
      .from('art_images')
      .select('*')
      .order('created_at', { ascending: true });
    if (error) throw new Error(`could not load the art library: ${error.message}`);
    cache = (data as ArtRow[]).map(toImage);
    return cache;
  })().finally(() => {
    inFlight = null;
  });

  return inFlight;
}

/** Drop the cache — call after any write so the next read sees it. */
export function invalidate(): void {
  cache = null;
}

/** What is already loaded, without waiting. Empty before the first load. */
export function cached(): readonly StoredArtImage[] {
  return cache ?? [];
}

// ---------------------------------------------------------------------------
// Writes — through the Worker, and only ever as a signed-in owner
// ---------------------------------------------------------------------------

/**
 * WHY EVERY WRITE CARRIES A TOKEN.
 *
 * Hiding the Art Library tab from everyone but the owner (`isOwner` in
 * `auth.ts`) stops a tab appearing. It does not stop a request: the four write
 * endpoints are named in the bundle and anyone can call them with curl. The
 * real gate is server-side, in `worker/owner.ts`, and it needs to be told WHO is
 * asking — that is this header, and nothing else in this file matters as much.
 *
 * WHAT IS SENT IS THE SESSION'S ACCESS TOKEN AND NOTHING ELSE. Not an email,
 * not a user id, and — emphatically — no password: a password does not appear
 * in this codebase and must never start. The Worker does not believe the token's
 * contents either; it asks Supabase to resolve it. So a forged token gets a 401
 * rather than an art library.
 *
 * SIGNED OUT SENDS NO HEADER AT ALL, rather than an empty or placeholder one.
 * "No token" and "a token I could not read" are different facts and the Worker
 * reports them differently; sending `Bearer undefined` would collapse a plain
 * "you are not signed in" into a confusing "your session was rejected".
 */
async function authHeaders(): Promise<Record<string, string>> {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    // Session storage blocked or corrupt. Nothing to send, and the Worker's
    // 401 will say so honestly — better than inventing a header.
    return {};
  }
}

/**
 * A write that was refused, with the refusal kept intact.
 *
 * `message` is the Worker's own sentence, shown to the person. `status` and
 * `reason` are the machine-readable halves, so a caller can do something
 * DIFFERENT for the two cases that look identical in a toast:
 *
 *   401 / `no-token` | `invalid-token` → sign in again. This is usually the
 *        owner's own session having expired mid-afternoon, and it is fixable
 *        by the person seeing it.
 *   403 / `not-owner` → signing in again changes nothing. Someone else's
 *        account is signed in, and the answer is "this library is not yours".
 *   503 / `verifier-unavailable` → nobody could be checked at all. Not the
 *        user's fault and not their problem to fix; try later.
 */
export class ArtWriteError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly reason: string | null,
  ) {
    super(message);
    this.name = 'ArtWriteError';
  }
}

/** Does this failure mean "sign in again"? The 401 half of the split. */
export function needsSignIn(e: unknown): boolean {
  return e instanceof ArtWriteError && e.status === 401;
}

/** Does this failure mean "you are somebody else"? The 403 half. */
export function isNotOwner(e: unknown): boolean {
  return e instanceof ArtWriteError && e.status === 403;
}

async function post<T>(path: string, body: BodyInit, headers?: HeadersInit): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    body,
    headers: { ...(headers as Record<string, string> | undefined), ...(await authHeaders()) },
  });
  let payload: { ok?: boolean; error?: string; reason?: string } & Record<string, unknown>;
  try {
    payload = await res.json();
  } catch {
    throw new ArtWriteError(
      `${path} returned ${res.status} and not JSON — is the Worker deployed?`,
      res.status,
      null,
    );
  }
  if (!res.ok || payload.ok === false) {
    // The Worker's sentence, verbatim. It knows which of the four things went
    // wrong and has already written the honest version; replacing it with a
    // generic one here would throw that away.
    throw new ArtWriteError(
      payload.error ?? `${path} failed with ${res.status}`,
      res.status,
      payload.reason ?? null,
    );
  }
  return payload as T;
}

export interface UploadResult {
  storagePath: string;
  url: string;
  originalName: string | null;
  bytes: number;
}

export async function uploadFile(file: File): Promise<UploadResult> {
  const form = new FormData();
  form.append('file', file);
  return post<UploadResult>('/api/art/upload', form);
}

export interface SaveTags {
  storagePath: string;
  scenes: SceneTag[];
  shot?: ShotType | null;
  intensity?: Intensity | null;
  trackCategories?: TrackCategory[];
  conditions?: Condition[];
  rarity?: Rarity;
  /** What a person calls it. Null clears it back to unnamed. */
  title?: string | null;
  credit?: string | null;
  notes?: string | null;
  originalName?: string | null;
  width?: number | null;
  height?: number | null;
}

export async function saveTags(tags: SaveTags): Promise<void> {
  await post('/api/art/save', JSON.stringify(tags), { 'Content-Type': 'application/json' });
  invalidate();
}

/**
 * Rename a photo already in the library.
 *
 * Its own endpoint rather than a `saveTags` call carrying the existing tags,
 * because a rename that has to resend the tags can lose them — one stale copy
 * of the library in a React state somewhere and a rename silently untags the
 * picture. This touches one column.
 */
export async function renameImage(storagePath: string, title: string | null): Promise<void> {
  await post('/api/art/rename', JSON.stringify({ storagePath, title }), {
    'Content-Type': 'application/json',
  });
  invalidate();
}

export async function deleteImage(storagePath: string): Promise<void> {
  await post('/api/art/delete', JSON.stringify({ storagePath }), { 'Content-Type': 'application/json' });
  invalidate();
}

export interface ArtWriteAccess {
  /** Both halves true: the Worker is configured AND you are the owner. */
  writable: boolean;
  /**
   * Machine-readable, from the Worker: `no-token`, `invalid-token`,
   * `not-owner`, `verifier-unavailable`, `no-secret`, or `unreachable` when the
   * question could not be asked at all. Null when writable.
   */
  reason: string | null;
  /** The sentence to show a person. Null when writable. */
  note: string | null;
  /** The verified email, when there is one. Null otherwise — never a guess. */
  signedInAs: string | null;
}

/**
 * CAN I ACTUALLY WRITE? Asked once, before the tagger renders its controls.
 *
 * This used to report only whether the Worker had a secret key, which stopped
 * being the whole truth the moment the endpoints started checking identity: a
 * signed-out visitor with a perfectly configured Worker got `writable: true`
 * and then a 401 on the first upload. So the token goes with the question and
 * the answer is about THIS caller.
 *
 * A failure to even ask returns `writable: false` with `reason: 'unreachable'`
 * — the honest answer to "can I write" when nobody replied is "I do not know,
 * so assume not", and the note says that rather than blaming the account.
 */
export async function artWriteAccess(): Promise<ArtWriteAccess> {
  try {
    const res = await fetch('/api/art/health', { headers: await authHeaders() });
    if (!res.ok) {
      return {
        writable: false,
        reason: 'unreachable',
        note: `The art endpoint answered ${res.status}. Nothing can be checked until it responds — is the Worker deployed?`,
        signedInAs: null,
      };
    }
    const body = (await res.json()) as Partial<ArtWriteAccess>;
    return {
      writable: body.writable === true,
      reason: body.reason ?? null,
      note: body.note ?? null,
      signedInAs: body.signedInAs ?? null,
    };
  } catch (e) {
    return {
      writable: false,
      reason: 'unreachable',
      note: `Could not reach the art endpoint (${String((e as Error).message ?? e)}). Nothing was written and nothing was checked.`,
      signedInAs: null,
    };
  }
}

/**
 * The boolean form, for callers that only need the yes/no.
 *
 * Kept because a boolean is what most call sites want, but note it throws away
 * the reason — anything that SHOWS the answer to a person should use
 * `artWriteAccess` instead, or it will end up printing one explanation for four
 * different problems, which is the thing this project does not ship.
 */
export async function artWritable(): Promise<boolean> {
  return (await artWriteAccess()).writable;
}

/**
 * Read a file's pixel dimensions in the browser before upload.
 *
 * Stored so the moment panel can reserve the right aspect box and avoid the
 * layout jump that otherwise happens on every single moment.
 */
export function readDimensions(file: File): Promise<{ width: number; height: number } | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    img.src = url;
  });
}
